<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowLeft, ArrowRight, CircleCheckBig } from '@lucide/vue';
import { resetPasswordSchema, type TokenInfo } from '@evolfin/shared';
import { http, EvolfinApiError } from '@/api/client';
import { useAltezzaAnimata } from '@/composables/altezzaAnimata';
import MarchioEvolfin from '@/components/app/MarchioEvolfin.vue';
import SfondoAmbiente from '@/components/app/SfondoAmbiente.vue';
import AvvisoErrore from '@/components/ui/AvvisoErrore.vue';
import CampoTesto from '@/components/ui/CampoTesto.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';

definePage({
  meta: { pubblica: true, titolo: 'Imposta password' },
});

const route = useRoute();
const router = useRouter();

// Il token arriva dal link dell'email (?token=…). Senza, la pagina non può fare
// nulla: lo si dice subito invece di mostrare un form che fallirebbe comunque.
const token = computed(() => (typeof route.query.token === 'string' ? route.query.token : ''));

// La stessa pagina serve due flussi: `reset` (password dimenticata) e `invito`
// (attivazione di un account nuovo). Il tipo lo dice l'API dall'ispezione del
// token, così la copy è quella giusta senza duplicare la pagina.
const info = ref<TokenInfo | null>(null);
const verificando = ref(true);
const eInvito = computed(() => info.value?.tipo === 'invito');
const tokenValido = computed(() => info.value?.valido === true);

const form = reactive({ password: '', conferma: '' });
const erroriCampo = ref<Record<string, string[]>>({});
const erroreGenerale = ref<string | null>(null);
const completato = ref(false);
const caricamento = ref(false);

const { riferimentoContenuto, altezza, animando } = useAltezzaAnimata();

const titolo = computed(() => (eInvito.value ? 'Attiva il tuo account' : 'Reimposta password'));
const sottotitolo = computed(() =>
  eInvito.value
    ? `Ciao ${info.value?.nome ?? ''}, imposta una password per accedere a Evolfin.`.trim()
    : 'Scegli una nuova password per il tuo account Evolfin.',
);
const ctaLabel = computed(() => (eInvito.value ? 'Attiva account' : 'Reimposta password'));
const ctaCaricamento = computed(() => (eInvito.value ? 'Attivazione…' : 'Aggiornamento…'));

onMounted(async () => {
  if (!token.value) {
    verificando.value = false;
    return;
  }
  try {
    const { data } = await http.get<TokenInfo>(
      `/api/auth/token/${encodeURIComponent(token.value)}`,
    );
    info.value = data;
  } catch {
    info.value = { valido: false, tipo: null, nome: null };
  } finally {
    verificando.value = false;
  }
});

const primoErrore = (chiave: string) => erroriCampo.value[chiave]?.[0];

async function invia(): Promise<void> {
  erroriCampo.value = {};
  erroreGenerale.value = null;

  const esito = resetPasswordSchema.safeParse({ token: token.value, password: form.password });
  if (!esito.success) {
    for (const problema of esito.error.issues) {
      const chiave = problema.path.join('.');
      (erroriCampo.value[chiave] ??= []).push(problema.message);
    }
    return;
  }
  // La conferma è un controllo di UI (non di dominio): sta solo qui, non in Zod.
  if (form.conferma !== form.password) {
    erroriCampo.value.conferma = ['Le due password non coincidono'];
    return;
  }

  caricamento.value = true;
  try {
    await http.post('/api/auth/reset-password/conferma', esito.data);
    completato.value = true;
  } catch (errore) {
    if (errore instanceof EvolfinApiError) {
      if (errore.dettagli.code === 'TOKEN_NON_VALIDO') {
        erroreGenerale.value = eInvito.value
          ? 'Il link di attivazione non è valido o è scaduto. Chiedi a un amministratore di reinviarlo.'
          : 'Il link non è valido o è scaduto. Richiedine uno nuovo dalla pagina "Password dimenticata".';
      } else if (errore.dettagli.statusCode === 429) {
        erroreGenerale.value = 'Troppe richieste ravvicinate. Attendi un minuto e riprova.';
      } else {
        erroriCampo.value = errore.erroriCampo;
        erroreGenerale.value = errore.message;
      }
    } else {
      erroreGenerale.value = 'Errore imprevisto. Riprova tra qualche istante.';
    }
  } finally {
    caricamento.value = false;
  }
}
</script>

<template>
  <div class="relative grid min-h-screen place-items-center overflow-hidden px-4 py-12">
    <SfondoAmbiente />

    <main class="relative w-full max-w-[400px]">
      <header class="entra mb-8 flex flex-col items-center text-center">
        <MarchioEvolfin :dimensione="60" />
        <h1 class="mt-6 font-display text-[34px] leading-[1.05] font-semibold tracking-[-0.03em]">
          {{ titolo }}
        </h1>
        <p class="mt-3 max-w-[20rem] text-ink-secondary">{{ sottotitolo }}</p>
      </header>

      <div class="entra card card--vetro bordo-riflesso p-7" style="animation-delay: 60ms">
        <div
          class="altezza-animata"
          :class="animando && 'altezza-animata--ritaglia'"
          :style="{ height: altezza }"
        >
          <div :ref="riferimentoContenuto">
            <!-- Esito positivo: la password è impostata, si torna al login. -->
            <div v-if="completato" class="flex flex-col items-center gap-3 text-center">
              <span
                class="grid size-11 place-items-center rounded-full bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
              >
                <CircleCheckBig :size="22" :stroke-width="1.8" aria-hidden="true" />
              </span>
              <p class="text-[15px] font-medium">
                {{ eInvito ? 'Account attivato' : 'Password aggiornata' }}
              </p>
              <p class="text-[13px] text-ink-secondary">Ora puoi accedere con la nuova password.</p>
              <PulsanteAzione class="mt-1" @click="router.push('/auth/login')">
                Vai all'accesso
                <template #icona>
                  <ArrowRight :size="16" :stroke-width="2" aria-hidden="true" />
                </template>
              </PulsanteAzione>
            </div>

            <!-- Verifica del token in corso. -->
            <p v-else-if="verificando" class="py-4 text-center text-[13px] text-ink-secondary">
              Verifica del link…
            </p>

            <!-- Token assente o non valido: nessun form. -->
            <div v-else-if="!token || !tokenValido" class="flex flex-col gap-4">
              <AvvisoErrore
                :messaggio="
                  !token
                    ? 'Link non valido: manca il token. Controlla di aver aperto il link completo.'
                    : 'Questo link non è valido o è scaduto.'
                "
              />
              <RouterLink
                to="/auth/password-dimenticata"
                class="inline-flex items-center justify-center gap-1.5 text-[13px] font-medium text-[var(--color-primary)] hover:underline"
              >
                Richiedi un nuovo link
                <ArrowRight :size="15" :stroke-width="2" aria-hidden="true" />
              </RouterLink>
            </div>

            <form v-else class="flex flex-col gap-4" novalidate @submit.prevent="invia">
              <AvvisoErrore v-if="erroreGenerale" :messaggio="erroreGenerale" />

              <CampoTesto
                v-model="form.password"
                etichetta="Nuova password"
                tipo="password"
                autocomplete="new-password"
                richiesto
                autofocus
                :errore="primoErrore('password')"
                nota="Almeno 12 caratteri."
              />
              <CampoTesto
                v-model="form.conferma"
                etichetta="Conferma password"
                tipo="password"
                autocomplete="new-password"
                richiesto
                :errore="primoErrore('conferma')"
              />

              <PulsanteAzione tipo="submit" larghezza-piena class="mt-1" :caricamento="caricamento">
                {{ caricamento ? ctaCaricamento : ctaLabel }}
                <template #icona>
                  <ArrowRight :size="16" :stroke-width="2" aria-hidden="true" />
                </template>
              </PulsanteAzione>

              <RouterLink
                to="/auth/login"
                class="inline-flex items-center justify-center gap-1.5 text-[13px] text-ink-muted hover:text-[var(--color-primary)]"
              >
                <ArrowLeft :size="15" :stroke-width="2" aria-hidden="true" />
                Torna all'accesso
              </RouterLink>
            </form>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
